import React, {type Dispatch, type SetStateAction, useCallback,
    useContext,
    useState,
} from 'react';


import {ErrorFlatButton} from 'src/components/error-flat-button';
import {FormInput} from 'src/components/input';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {CheckboxSkeleton, FormItemSkeleton, Skeleton, SkeletonItem} from 'src/components/skeleton';
import {CustomValidatedControl, ValidationContext} from 'src/contexts/validation-context';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights/constants';
import {validateMicrositeDomain} from 'src/openapi/admin/apis/MicroSitesDomainsApi';
import {useAppDispatch} from 'src/store/hooks';

import {FlatButton} from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';

import {
    MICROSITE_APPROVED_DOMAINS_CONTROL,
    MICROSITE_APPROVED_DOMAINS_DUPLICATE,
    MICROSITE_APPROVED_DOMAINS_DUPLICATE_MESSAGE,
    MICROSITE_APPROVED_DOMAINS_EXISTS,
} from './constants';

import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {Error} from 'src/openapi/admin/models/Error';
import type {MicroSiteDomains} from 'src/openapi/admin/models/MicroSiteDomains';
import type {CheckBoxChangeData} from 'src/typedef';

type Props = {
    isLoading: boolean,
    loadingError: Error | null,
    data: MicroSiteDomains,
    initData: MicroSiteDomains,
    setData: Dispatch<SetStateAction<MicroSiteDomains>>,
    trackChange: (parameter: string) => void,
    readonly?: boolean,
};

export const ApprovedDomains = (props: Props) => {
    const {
        isLoading,
        loadingError,
        data: {domains: approvedDomains, disabled},
        initData: {domains: initApprovedDomains},
        setData,
        readonly,
        trackChange,
    } = props;

    const apiDispatch = useAppDispatch();
    const {addError, removeError} = useContext(ValidationContext);
    const [checkingMicroSites, setCheckingMicroSites] = useState<number[]>([]);

    const changeDomain = useCallback(async (domain: string, index: number) => {
        trackChange('Change Microsite Approved Domains');
        const updatedDomains = approvedDomains.map((dom, i) => (
            i === index ? domain : dom),
        );
        if (domain && updatedDomains.filter(updatedDomain => updatedDomain === domain).length > 1) {
            addError({
                controlId: `${MICROSITE_APPROVED_DOMAINS_CONTROL}-${index}`,
                code: MICROSITE_APPROVED_DOMAINS_DUPLICATE,
                message: MICROSITE_APPROVED_DOMAINS_DUPLICATE_MESSAGE,
            });
        } else {
            updatedDomains.forEach((validationCandidate, id) => {
                if (updatedDomains.filter(updatedDomain => updatedDomain === validationCandidate).length === 1) {
                    removeError(`${MICROSITE_APPROVED_DOMAINS_CONTROL}-${id}`, [MICROSITE_APPROVED_DOMAINS_DUPLICATE]);
                }
            });
            const prevDomain = approvedDomains[index];
            if (prevDomain !== domain) {
                approvedDomains.forEach((validationCandidate, id) => {
                    if (prevDomain === validationCandidate) {
                        removeError(
                            `${MICROSITE_APPROVED_DOMAINS_CONTROL}-${id}`,
                            [MICROSITE_APPROVED_DOMAINS_DUPLICATE],
                        );
                    }
                });
            }
        }

        removeError(`${MICROSITE_APPROVED_DOMAINS_CONTROL}-${index}`, [MICROSITE_APPROVED_DOMAINS_EXISTS]);
        if (!initApprovedDomains.includes(domain) && domain) {
            setCheckingMicroSites(prevState => [...prevState, index]);
            const serverValidation = await apiDispatch(validateMicrositeDomain({domain}));
            setCheckingMicroSites(prevState => prevState.filter(i => i !== index));

            if (serverValidation.payload.error) {
                addError({
                    controlId: `${MICROSITE_APPROVED_DOMAINS_CONTROL}-${index}`,
                    code: MICROSITE_APPROVED_DOMAINS_EXISTS,
                    message: serverValidation.payload.data.message || 'Invalid domain',
                });
            }
        }

        setData(state => ({...state, domains: updatedDomains}));
    }, [approvedDomains, initApprovedDomains, addError, apiDispatch, setData, removeError, trackChange]);

    const removeDomain = useCallback((index: number) => {
        trackChange('Remove Approved Domain');
        setData(state => ({
            ...state,
            domains: state.domains.filter((_, i) => i !== index),
        }));
        removeError(`${MICROSITE_APPROVED_DOMAINS_CONTROL}-${index}`);
    }, [removeError, setData, trackChange]);

    const addDomain = useCallback(() => {
        trackChange('Add Approved Domain');
        setData(state => ({
            ...state,
            domains: [...state.domains, ''],
        }));
    }, [setData, trackChange]);

    const changeDisabled = useCallback((data: CheckBoxChangeData) => {
        const domainsDisabled = data.event.currentTarget.checked;
        trackChange('Disable Approved Domains');
        setData(state => ({
            domains: domainsDisabled ? [''] : state.domains,
            disabled: domainsDisabled,
        }));
    }, [setData, trackChange]);

    return (
        <Skeleton
            shown={isLoading}
            view={(
                <Group direction="v" alignItems="start">
                    <CheckboxSkeleton labelWidth={140}/>
                    <FormItemSkeleton labelWidth={106}>
                        <SkeletonItem width={300} height={30}/>
                    </FormItemSkeleton>
                </Group>
            )}>
            <ServerResponseHandler
                sectionName="Approved Domains Section"
                smallView={true}
                accessRight={ACCESS_RIGHTS.MICROSITES_SCREEN_VIEW as AccessRight}
                serverError={loadingError}>
                <Group direction="v" alignItems="start">
                    <CheckBox
                        label="Disable Approved Domains"
                        checked={disabled}
                        disabled={readonly}
                        onCheckBoxChange={changeDisabled}
                    />
                    <Item style={{width: 330}}>
                        <Label>Approved Domains</Label>
                        <Group direction="v" alignItems="start" gap="xs">
                            {approvedDomains.map((domain, index) => (
                                <Group key={index} alignItems="start">
                                    <CustomValidatedControl name={`${MICROSITE_APPROVED_DOMAINS_CONTROL}-${index}`}>
                                        {error => (
                                            <FormInput
                                                width={230}
                                                placeholder="Domain URL"
                                                value={domain}
                                                busy={checkingMicroSites.includes(index)}
                                                readOnly={readonly}
                                                disabled={disabled}
                                                onBlur={value => changeDomain(value.trim(), index)}
                                                valid={error ? false : undefined}
                                                errorDescription={error}
                                            />
                                        )}
                                    </CustomValidatedControl>
                                    {approvedDomains.length > 1 && !readonly && !disabled && (
                                        <Item className="pt-xs">
                                            <ErrorFlatButton
                                                icon={Icons.TIMES_CIRCLE}
                                                label="Remove"
                                                onClick={() => removeDomain(index)}
                                            />
                                        </Item>
                                    )}
                                </Group>
                            ))}
                            <FlatButton
                                disabled={readonly || approvedDomains.includes('') || disabled}
                                onClick={addDomain}
                                startIcon={<Icon type={Icons.PLUS_CIRCLE}/>}>
                                <b>Add Domain</b>
                            </FlatButton>
                        </Group>
                    </Item>
                </Group>
            </ServerResponseHandler>
        </Skeleton>
    );
};
